<script setup lang="ts">
  import { computed, defineProps, type PropType, ref } from 'vue'
  import { type RuleExpression, useField } from 'vee-validate'
  import { useTranslation } from '@/composables/useTranslation'
  import ImageIcon from '@/components/icons/ImageIcon.vue'
  import TrashIcon from '@/components/icons/TrashIcon.vue'
  import { useThemeColor } from '@/composables/useThemeColorV3'

  const { t } = useTranslation({
    en: {
      dropFileHere: 'Drop your file here or',
      browse: 'browse',
      allowedFiles: 'Allowed file extensions: {allowedFileExtensions}',
      fileDetails: '{type}, {size} bytes'
    },
    de: {
      dropFileHere: 'Ziehen Sie Ihre Datei hier in das Fenster oder',
      browse: 'durchsuchen',
      allowedFiles: 'Erlaubte Dateien: {allowedFileExtensions}',
      fileDetails: '{type}, {size} Bytes'
    }
  })

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    allowedFileExtensions: {
      type: Array,
      required: false,
      default: () => ['pdf']
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue'])

  const { value: model, errorMessage, handleBlur, meta } = useField(props.name, props.rules)
  const isDragging = ref(false)
  const fileBlob = ref<Blob | null>(null)
  const fileName = ref<string | null>(null)

  // Theme color
  const themeColor = useThemeColor()

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = true
  }

  const handleDragLeave = () => {
    isDragging.value = false
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = false
    if (event.dataTransfer && event.dataTransfer.files.length) {
      const file = event.dataTransfer.files[0]
      fileBlob.value = file
      fileName.value = file.name
      model.value = fileBlob.value
    }
    emitUpdate(fileBlob.value)
  }

  const handleFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    if (input.files && input.files[0]) {
      const file = input.files[0]
      fileBlob.value = file
      fileName.value = file.name
      model.value = fileBlob.value
    }
    emitUpdate(fileBlob.value)
  }

  const resetFile = () => {
    fileBlob.value = null
    fileName.value = null
    model.value = null
    emitUpdate(null)
  }

  const emitUpdate = (value: any) => {
    emit('update:modelValue', value)
  }

  const hasFile = computed(() => !!fileBlob.value)
</script>

<template>
  <!--  {{ localeStore.getLocale }}-->
  <div class="space-y-2">
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium text-gray-800"
    >
      {{ label }}
    </label>
    <div v-if="hasFile">
      <div
        class="group relative flex items-center gap-x-3 rounded-xl border border-gray-200 bg-white p-3"
      >
        <span
          class="flex size-7 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white
            text-gray-500 sm:h-[38px] sm:w-[38px]"
        >
          <ImageIcon class="size-3.5 flex-shrink-0 sm:size-5" />
        </span>

        <div class="grow truncate">
          <p class="block truncate text-sm font-semibold text-gray-800">
            {{ fileName }}
          </p>
          <p class="block truncate text-xs text-gray-500">
            {{ model?.type }}, {{ model?.size }} bytes
          </p>
        </div>

        <div>
          <div
            class="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 sm:p-1 lg:shadow"
          >
            <div class="inline-block">
              <button
                class="inline-flex size-[30px] items-center justify-center gap-x-2 rounded-lg border border-transparent
                  text-red-600 hover:bg-red-100"
                type="button"
                @click="resetFile"
              >
                <TrashIcon class="size-4 flex-shrink-0" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex justify-center rounded-xl border border-dashed bg-white p-12"
      :class="{
        'border-2 border-red-500 focus:border-red-500 focus:ring-red-500': errorMessage,
        [`border-${themeColor}-500 bg-${themeColor}-50`]: isDragging && !errorMessage,
        [`border-gray-300 focus:border-${themeColor}-600 focus:ring-${themeColor}-600`]:
          !errorMessage && !isDragging
      }"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div class="text-center">
        <div class="mt-4 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
          <span class="pe-1 font-medium text-gray-800">
            {{ t('dropFileHere') }}
          </span>
          <label
            :class="`relative m-0 cursor-pointer rounded-lg bg-white p-0 font-semibold text-${themeColor}-600
              hover:text-${themeColor}-700 hover:underline`"
          >
            <span>
              {{ t('browse') }}
            </span>
            <input
              class="sr-only"
              type="file"
              :accept="props.allowedFileExtensions.map((ext) => '.' + ext).join(', ')"
              @change="handleFileChange"
              @blur="handleBlur"
            />
          </label>
        </div>
        <p class="mt-1 text-xs text-gray-400">
          {{ t('allowedFiles', { allowedFileExtensions: allowedFileExtensions.join(', ') }) }}
        </p>
      </div>
    </div>
    <p
      v-if="errorMessage"
      class="mt-1 text-xs text-red-600"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped></style>
